import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router';

import { ApiError } from '@shared/api/client';
import { Breadcrumb } from '@shared/chrome/Breadcrumb';
import { resolveErrorCopy } from '@shared/errors/catalog';
import { formatNumber } from '@shared/format';
import { pageCountOf, useClampPage, usePageParam } from '@shared/pagination';
import { useForm } from 'react-hook-form';
import { Alert } from '@shared/ui/Alert';
import { Dialog, isCancelledSubmit } from '@shared/ui/dialog';
import { Pager } from '@shared/ui/pager';
import { SurfaceError } from '@shared/ui/SurfaceError';

import { sharedCopy } from '../_shared/copy';
import type { HeldComment } from '../_shared/types';
import { ModerationCard } from './Comments.Card';
import { CommentsSkeleton } from './Comments.Skeleton';
import { copy } from './copy';
import { errors } from './errors';
import { rejectFormSchema, type RejectFormValues } from './forms.schema';
import { useReleaseComment, useRemoveComment } from './mutations';
import { moderationQueueKey, useModerationQueue } from './queries';

/**
 * `/comments` — the comment moderation queue, operating **before** publication.
 *
 * This is the surface behind the product's own promise that comments are
 * approved before they appear. A held comment is visible to its author and to
 * nobody else; approving sends it where its author already meant it to go, and
 * rejecting withholds it while leaving it in their own view — it never
 * disappears, because disappearance reads as a fault or as silent censorship.
 *
 * **Empty is the good state.** An empty queue means the work is done, so the
 * empty block states that and offers no primary action — a call to action there
 * would invent work.
 *
 * **Focus survives a handled card.** Removing the node that held focus would
 * drop the reader to `<body>` and back to the top of the queue; the next item's
 * approve control takes it instead, and when the queue empties focus lands on
 * the empty state.
 *
 * **No selection column, no bulk bar, no sort, no filter.** All are
 * notification-inbox rows; drawing them here would be borrowing another
 * surface's scope, and a bulk moderation decision is the last decision that
 * should be one click.
 *
 * **The pager is not one of those.** The service pages this queue by offset, and
 * reading only page one meant every comment behind it stayed held forever —
 * invisible to everyone but its author, never approved and never refused. The
 * queue is still one decision at a time; it is now a queue that can be worked to
 * the bottom.
 *
 * **A decision re-slices every page, so it invalidates all of them.** A handled
 * card leaves the queue and pulls the rows after it forward; invalidating only
 * the page in hand would walk the operator into rows that have already moved.
 */
export function CommentsPage() {
  // The ordinal lives in the URL: this queue is offset-paged, so «صفحة ٣ من
  // الطابور» is a location that can be reloaded, linked, and returned to with
  // Back — none of which it was while the number sat in component state.
  const { page, goToPage } = usePageParam();

  const query = useModerationQueue(page);
  const queryClient = useQueryClient();

  const release = useReleaseComment();
  const remove = useRemoveComment();

  const [announcement, setAnnouncement] = useState('');
  const [pending, setPending] = useState<HeldComment | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  /** The index whose approve control should take focus after a card leaves. */
  const focusIndex = useRef<number | null>(null);
  const approveButtons = useRef<(HTMLButtonElement | null)[]>([]);
  const emptyStateRef = useRef<HTMLDivElement>(null);

  const items = query.data?.items ?? [];
  // The queue's whole depth. It is also the only total on this payload — the
  // route states `meta.total_held` and no `total` — so it is what the pager
  // divides by, and «صفحة ١» with no «من N» was the absence of exactly this.
  const totalHeld = query.data?.meta?.total_held;
  const totalPages = pageCountOf(totalHeld, query.data?.page_size);
  useClampPage(page, totalPages);

  useEffect(() => {
    const index = focusIndex.current;
    if (index === null) return;
    focusIndex.current = null;

    if (items.length === 0) {
      emptyStateRef.current?.focus();
      return;
    }

    // The card that took the handled one's place, or the last one when the
    // handled card was at the foot of the queue.
    const target = approveButtons.current[Math.min(index, items.length - 1)];
    target?.focus();
  }, [items.length]);

  function onDecisionFailure(error: unknown) {
    const status = error instanceof ApiError ? error.status : 0;
    const code = error instanceof ApiError ? error.code : undefined;
    setAnnouncement(resolveErrorCopy(code, status, errors).message);
  }

  function approve(item: HeldComment, index: number) {
    focusIndex.current = index;

    release.mutate(item.id, {
      onSuccess: () => setAnnouncement(copy.approved),
      onError: (error) => {
        focusIndex.current = null;
        onDecisionFailure(error);
      },
    });
  }

  function commitReject(values: RejectFormValues) {
    const item = pending;
    if (!item) return;

    setFormError(null);
    focusIndex.current = items.findIndex((row) => row.id === item.id);

    remove.mutate(
      { commentId: item.id, reason: values.reason },
      {
        onSuccess: (result) => {
          setPending(null);
          setAnnouncement(
            result.released_removed_count > 0
              ? copy.rejected(formatNumber(result.released_removed_count))
              : copy.rejectedAlone,
          );
        },
        onError: (error) => {
          focusIndex.current = null;
          const status = error instanceof ApiError ? error.status : 0;
          const code = error instanceof ApiError ? error.code : undefined;
          // The refusal stays on the dialog: the operator's reason is still on
          // screen and still theirs to correct.
          setFormError(resolveErrorCopy(code, status, errors).message);
        },
      },
    );
  }

  const busy = release.isPending || remove.isPending;

  return (
    <>
      <Breadcrumb items={[{ label: sharedCopy.crumbs.console, to: '/' }, { label: copy.crumb }]} />

      <div className="mb-6 mt-3 grid gap-stack-tight">
        <h1 className="font-display text-h1 font-bold">{copy.head.title}</h1>
        <p className="mb-0 max-w-measure text-[1.0625rem] text-ink-muted">{copy.head.lede}</p>
      </div>

      {/*
        Informational, so no `role="alert"`: an informational banner that
        interrupts a screen reader for no reason is a defect of its own.
      */}
      <Alert kind="info" title={copy.notice.title}>
        {copy.notice.body}
      </Alert>

      {/* One polite region serves both decisions — each is a result the operator
          intended, never an error interrupting them. */}
      <p className="sr-only" aria-live="polite">
        {announcement}
      </p>

      <section aria-labelledby="moderation-heading" className="mt-6">
        <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2">
          <h2 id="moderation-heading" className="font-display text-h2 font-bold">
            {copy.section.heading}
          </h2>

          {/*
            A static count capsule, never a control. The figure is the queue's
            whole depth — the same number the console entry reports, returned
            from one query so the two surfaces cannot disagree — and it carries
            a visually-hidden phrase naming what it counts, because a bare
            figure never stands alone.
          */}
          {totalHeld === undefined ? null : (
            <span className="rounded-pill border border-border bg-surface-overlay px-3 py-1 text-xs text-ink-muted">
              <bdi>{formatNumber(totalHeld)}</bdi>
              <span className="sr-only"> {copy.section.heading}</span>
            </span>
          )}
        </div>

        {query.isError ? (
          <SurfaceError
            error={query.error}
            catalog={errors}
            onRetry={() => void queryClient.invalidateQueries({ queryKey: moderationQueueKey(page) })}
          />
        ) : null}

        {query.isPending ? <CommentsSkeleton /> : null}

        {query.data ? (
          items.length === 0 ? (
            <EmptyQueue ref={emptyStateRef} {...(page > 1 ? { onBack: () => goToPage(page - 1) } : {})} />
          ) : (
            <>
              <p className="mb-4 max-w-measure text-xs text-ink-muted">
                {copy.truncation.prefix} <bdi>{formatNumber(items.length)}</bdi> {copy.truncation.suffix}
              </p>

              <div className="grid gap-4">
                {items.map((item, index) => (
                  <ModerationCard
                    key={item.id}
                    item={item}
                    busy={busy}
                    approveRef={(element) => {
                      approveButtons.current[index] = element;
                    }}
                    onApprove={() => approve(item, index)}
                    onReject={() => {
                      setFormError(null);
                      setPending(item);
                    }}
                  />
                ))}
              </div>
            </>
          )
        ) : null}

        {/*
          Outside the card check on purpose: handling the last card on a walked
          page empties it, and the control that walks back is the only way off.
        */}
        {query.data ? (
          <Pager
            label={copy.pager.label}
            page={page}
            pageCount={totalPages}
            shown={items.length}
            total={totalHeld}
            noun={{
              one: copy.pager.shownOne,
              two: copy.pager.shownTwo,
              few: copy.pager.shownFew,
              many: copy.pager.shownMany,
            }}
            hasPrevious={page > 1}
            hasNext={Boolean(query.data.has_next)}
            onPrevious={() => goToPage(page - 1)}
            onNext={() => goToPage(page + 1)}
            onGoToPage={goToPage}
          />
        ) : null}
      </section>

      <RejectDialog
        item={pending}
        busy={remove.isPending}
        formError={formError}
        onCommit={commitReject}
        onCancel={() => {
          setFormError(null);
          setPending(null);
        }}
      />
    </>
  );
}

/**
 * Empty because nothing is waiting — the good state.
 *
 * Three parts: a mark, the reason in human language, and one way out. It takes a
 * tab stop so focus can land here when the last card is handled, which is where
 * the reader now is.
 *
 * **A walked page that emptied is a different fact and says so.** «انتهى الطابور»
 * on page four would tell an operator the work is done when what happened is
 * that this page's cards were handled and the queue's earlier pages were not
 * touched. It is the same block with the other sentence and the other way out —
 * back one page, rather than back to the console.
 */
function EmptyQueue({
  ref,
  onBack,
}: {
  ref: React.RefObject<HTMLDivElement | null>;
  /** Present only on a walked page; its absence is what makes this the good state. */
  onBack?: () => void;
}) {
  return (
    <div ref={ref} tabIndex={-1} className="rounded-md border border-border bg-surface p-6">
      <h3 className="mb-2 font-display text-h3 font-semibold">
        {onBack ? copy.states.emptyPageTitle : copy.states.emptyTitle}
      </h3>
      <p className="mb-4 max-w-measure text-sm text-ink-muted">
        {onBack ? copy.states.emptyPageBody : copy.states.emptyBody}
      </p>

      {onBack ? (
        <button
          type="button"
          onClick={onBack}
          className="hit-target inline-flex items-center rounded-sm border border-border-strong bg-surface-raised px-4 text-sm font-semibold text-ink hover:bg-surface-overlay"
        >
          {copy.states.emptyPageAction}
        </button>
      ) : (
        <Link
          to="/"
          className="hit-target inline-flex items-center rounded-sm border border-border-strong bg-surface-raised px-4 text-sm font-semibold text-ink no-underline hover:bg-surface-overlay"
        >
          {copy.states.emptyAction}
        </Link>
      )}
    </div>
  );
}

/**
 * Reject — the destructive half of the pair, and the only one behind a dialog.
 *
 * The heading names the author, and the body states the cascade: every reply
 * hanging off the comment goes with it, because a reply orphaned under a removed
 * parent is a fragment of a conversation whose other half no longer exists.
 *
 * **The reason is required, and it is free text rather than a picker.** No
 * configured vocabulary names a rejection reason, so no list is drawn; the
 * owning service requires a written one because moderation decisions are
 * recorded in-row and nowhere else in v1 — a terminal decision with no stated
 * cause is a decision nobody can review. It is never shown to the comment's
 * author.
 */
function RejectDialog({
  item,
  busy,
  formError,
  onCommit,
  onCancel,
}: {
  item: HeldComment | null;
  busy: boolean;
  formError: string | null;
  onCommit: (values: RejectFormValues) => void;
  onCancel: () => void;
}) {
  const headingId = useId();
  const fieldId = useId();
  const hintId = useId();
  const errorId = useId();

  const { register, handleSubmit, reset, formState } = useForm<RejectFormValues>({
    resolver: zodResolver(rejectFormSchema),
    defaultValues: { reason: '' },
  });

  useEffect(() => {
    if (!item) return;
    reset({ reason: '' });
  }, [item, reset]);

  if (!item) return null;

  const fieldError = formState.errors.reason?.message;
  const describedBy = [hintId, fieldError ? errorId : null].filter(Boolean).join(' ');

  return (
    <Dialog
      open
      labelledBy={headingId}
      onClose={(returnValue) => {
        if (returnValue !== 'reject') onCancel();
      }}
    >
      <form
        method="dialog"
        onSubmit={(event) => {
          // «ألغِ» is a submit too — see `isCancelledSubmit`. Left alone, it lets
          // `method="dialog"` close the element instead of rejecting on the way out.
          if (isCancelledSubmit(event, 'reject')) return;
          event.preventDefault();
          void handleSubmit(onCommit)(event);
        }}
        className="flex flex-col gap-4"
      >
        <div>
          <h2 id={headingId} className="text-h3">
            {copy.dialog.heading(item.author.display_name ?? '')}
          </h2>
          <p className="mb-0 mt-1 max-w-none text-xs text-ink-muted">{copy.dialog.requiredNote}</p>
        </div>

        <p className="mb-0 max-w-none text-sm text-ink-muted">{copy.dialog.body}</p>

        {formError ? (
          <p role="alert" className="mb-0 max-w-none text-sm text-danger">
            {formError}
          </p>
        ) : null}

        <div className="grid gap-2">
          <label htmlFor={fieldId} className="text-sm font-medium text-ink">
            {copy.dialog.reasonLabel}
            <span aria-hidden="true" className="ms-px text-danger">
              *
            </span>
          </label>

          <textarea
            id={fieldId}
            rows={3}
            required
            aria-describedby={describedBy}
            aria-invalid={Boolean(fieldError)}
            aria-required
            className="w-full rounded-sm border border-border-strong bg-surface p-3 text-sm leading-normal text-ink [resize:vertical]"
            {...register('reason')}
          />

          <p id={hintId} className="mb-0 max-w-none text-xs text-ink-muted">
            {copy.dialog.reasonHint}
          </p>

          {fieldError ? (
            <p id={errorId} className="mb-0 max-w-none text-xs text-danger">
              {fieldError}
            </p>
          ) : null}
        </div>

        <div className="flex flex-wrap gap-2">
          {/*
            `formNoValidate` is load-bearing here rather than defensive: this
            dialog holds a `required` control, and both buttons in a
            `method="dialog"` form are submits. Without it the browser blocks the
            **exit** and demands a reason from an operator who has just decided
            not to reject — the dialog becomes a trap whose only signposted exits
            are `Esc` and the backdrop.
          */}
          <button
            type="submit"
            value="cancel"
            formNoValidate
            onClick={onCancel}
            className="rounded-sm border border-border-strong px-5 py-3 text-sm text-ink hover:bg-hover-bg"
          >
            {sharedCopy.action.cancel}
          </button>

          <button
            type="submit"
            value="reject"
            disabled={busy}
            className="rounded-sm border border-danger px-5 py-3 text-sm text-danger hover:bg-danger-soft disabled:cursor-not-allowed disabled:opacity-45"
          >
            {copy.dialog.commit}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
